import { format, utcToZonedTime } from 'date-fns-tz';
import Highcharts, { Options as HighchartsOptions, Point, XAxisOptions } from 'highcharts';
import highchartsGantt from 'highcharts/modules/gantt';
import HighchartsReact from 'highcharts-react-official';
import React, { memo, useEffect } from 'react';

import { chartConfig } from './config';
import { GanttChartProps, ScheduleElementType } from './types';
import { useAuth } from '../../app/AuthorizationProvider';
import { CHART_BACKGROUND_COLOR, HOURS_24 } from '../../scenes/Scheduling/config';

highchartsGantt(Highcharts);

const styleChart = (chart: Highcharts.Chart) => {
  const top = chart.plotTop;
  const options = {
    fill: CHART_BACKGROUND_COLOR,
  };

  const svg = document.getElementsByClassName('highcharts-root')[0];
  const xAxisMinutes = svg
    .querySelector('.highcharts-axis.highcharts-xaxis.zoom-xAxis-minutes')!
    .getBoundingClientRect().height;

  // set background color for xAxis minutes section (adding new rectangle underneath the axis elements)
  chart.renderer
    .rect(0, top - xAxisMinutes, chart.chartWidth, xAxisMinutes)
    .add()
    .attr(options);
};

const GanttChartComponent: React.FC<GanttChartProps> = ({ elements, chartRef, t, timezone }) => {
  const { localeObj } = useAuth();

  const options: HighchartsOptions = {
    ...chartConfig,
    series: [
      {
        type: 'gantt',
        data: elements,
      },
    ],
    time: {
      useUTC: true,
    },
    tooltip: {
      ...chartConfig.tooltip,
      formatter() {
        const { color } = this;
        // eslint-disable-next-line
        const point = this.point as Point & ScheduleElementType;

        const date = format(utcToZonedTime(point.date, timezone), t('date.formats.default'), {
          locale: localeObj,
          timeZone: timezone,
        });
        const startTime = format(utcToZonedTime(point.custom.startDateTime, timezone), t('date.formats.timeShort'), {
          locale: localeObj,
          timeZone: timezone,
        });
        const endTime = format(utcToZonedTime(point.custom.endDateTime, timezone), t('date.formats.timeShort'), {
          locale: localeObj,
          timeZone: timezone,
        });
        const durationInMinutesTotal = (point.custom.endDateTime - point.custom.startDateTime) / 1000 / 60;
        const durationInMinutes = durationInMinutesTotal % 60;
        const durationInHours = (durationInMinutesTotal - durationInMinutes) / 60;
        const durationInHoursLabel = durationInHours ? `${durationInHours} ${t('time.hrs')}` : '';
        const durationInMinutesLabel = durationInMinutes ? `${durationInMinutes} ${t('time.min')}` : '';
        const productive = t(`schedule.element.${point.productive ? 'productive' : 'nonProductive'}`);
        const paid = t(`schedule.element.${point.paid ? 'paid' : 'unpaid'}`);

        return (
          `<div class="highcharts-tooltip-inner" style="border-color: ${color}">` +
          `<p><label>${t('schedule.element.element')}:</label><br><b>${point.scheduleElementName}</b></p>` +
          `<p><label>${t('date.date')}: <b>${date}</b></label></p>` +
          `<p><label>${t('time.duration')}: <b>${startTime} - ${endTime}</b></label><br>` +
          `<b><big>${durationInHoursLabel} ${durationInMinutesLabel}</big></b></p>` +
          `<p><label>${t('layout.type')}:</label><br><b>${productive} - ${paid}</b></p>` +
          `</div>`
        );
      },
    },
    xAxis: [
      {
        ...(chartConfig.xAxis as XAxisOptions[])[0],
        min: 0,
        max: HOURS_24,
      },
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      styleChart(chartRef.current.chart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HighchartsReact
      containerProps={{
        'data-test-id': 'my-schedule:weekly-view:gantt-chart',
      }}
      ref={chartRef}
      key={new Date().getTime()}
      highcharts={Highcharts}
      options={options}
      constructorType="ganttChart"
    />
  );
};

export const GanttChart = memo(GanttChartComponent);
